// #DUC_VALIDATOR_V1 — the data-hygiene gate for every module's front door (standing rule).
// A Dakota job number (DUC), CP stripped, is exactly 8 chars:
//   [E F G P S W]  +  2-digit year (>=40, i.e. 2020+)  +  3-digit day-of-year (001-366)
//   +  a 2-char sequence: two digits, OR an Auger tag A0-A9 / AU.
// Examples that pass: W4602895, E4514703, W45301A4 (Auger), W46169A2 (Auger), F41078AU.
//
// IMPORTANT: format is a HINT, not the source of truth. The real question for weekly expenses is
// "does this number match a real job?" — that answer comes from the live Job List, not this regex.
// A handful of legit legacy Auger numbers (e.g. S46125A19) don't fit the 8-char shape; those must
// still validate if they're on the Job List. So: check Job-List membership FIRST, format SECOND.

export const stripCP = (s) =>
  String(s || '').trim().toUpperCase().replace(/^CP-/, '').replace(/-CP$/, '');

// #JOBNUM_V1 — the number a HUMAN should see for a job or log. Fork jobs are stored under a
// custom-<timestamp> key with the real number in displayDuc; that key is a storage id, never a
// job number, so it must never reach the screen. Use this at every render site.
// (Matching/keying still uses .duc — do NOT swap this in for lookups or React keys.)
export const jobNum = (j) => {
  if (!j) return '';
  const d = String(j.duc || '');
  const disp = String(j.displayDuc || '').trim();
  if (disp) return disp;
  return d.startsWith('custom-') ? '(unnumbered)' : d;
};

// Pure format check against the standing-rule shape. Returns { ok, reason }.
export const ducFormat = (raw) => {
  const d = stripCP(raw);
  if (!d) return { ok: false, reason: 'blank' };
  if (d.length !== 8) return { ok: false, reason: `${d.length} chars (need 8)` };
  if (!/^[EFGPSW]/.test(d)) return { ok: false, reason: `bad prefix '${d[0]}'` };
  const yr = Number(d.slice(1, 3));
  if (!(yr >= 40 && yr <= 99)) return { ok: false, reason: `year ${d.slice(1, 3)}` };
  const doy = Number(d.slice(3, 6));
  if (!/^\d{3}$/.test(d.slice(3, 6)) || doy < 1 || doy > 366) return { ok: false, reason: `day ${d.slice(3, 6)}` };
  const seq = d.slice(6, 8);
  if (!/^(\d{2}|A[0-9]|AU)$/.test(seq)) return { ok: false, reason: `seq '${seq}'` };
  return { ok: true, reason: '' };
};

// The one to call at a module front door. `jobKeys` is a Set of CP-stripped job numbers from the
// live Job List. Returns a status the UI can color:
//   'match'    — on the Job List (the real job; show its customer/status)   [green]
//   'unlisted' — valid format but NOT on the Job List (new job? typo?)      [amber]
//   'invalid'  — fails the format gate and isn't on the list                [red]
export const checkDuc = (raw, jobKeys) => {
  const norm = stripCP(raw);
  const onList = jobKeys instanceof Set ? jobKeys.has(norm) : false;
  if (onList) return { norm, status: 'match', reason: '' };
  const f = ducFormat(norm);
  if (f.ok) return { norm, status: 'unlisted', reason: 'not on Job List' };
  return { norm, status: 'invalid', reason: f.reason };
};
